import * as composeWindow from "../lib/composeWindow";
import { plural } from "../lib/format";
import * as nav from "../lib/navigation";
import * as reminders from "../lib/reminders";
import * as retention from "../lib/retention";
import {
  accountCollapsed,
  mailboxCollapsed,
  theme,
  toggleAccountCollapsed,
  toggleMailboxCollapsed,
} from "../lib/settings";
import * as store from "../lib/store";
import type { Mailbox } from "../lib/types";
import { AccountDot } from "./AccountColour";
import * as Icon from "./Icons";

/**
 * Do something, then leave the folder pane.
 *
 * Every control in here answers the question the pane was opened to ask, so
 * every one of them ends it. On a wide screen `leaveFolders` is a no-op, since
 * the sidebar was never a pane to leave.
 */
function choose(run: () => void): () => void {
  return () => {
    run();
    nav.leaveFolders();
  };
}

/**
 * Roles that get a unified entry, in the order a mail client shows them.
 *
 * `flagged` used to be one of these, labelled "Starred", and it was never once
 * drawn: it means a mailbox whose *role* is flagged, almost no provider has
 * such a folder, and the row is only rendered where some account does. Starred
 * is a real entry below now, and it is a query rather than a folder -- a star
 * is a keyword the message carries wherever it is filed, so the view has to
 * span every folder to mean what the word means. Two rows called Starred would
 * have been the worse outcome, so the role is gone from here.
 */
const UNIFIED: { role: string; label: string; icon: typeof Icon.Inbox }[] = [
  { role: "inbox", label: "Inbox", icon: Icon.Inbox },
  { role: "drafts", label: "Drafts", icon: Icon.Pencil },
  { role: "sent", label: "Sent", icon: Icon.Send },
  { role: "archive", label: "Archive", icon: Icon.Archive },
  { role: "trash", label: "Trash", icon: Icon.Trash },
];

export function Sidebar() {
  const scope = store.scope.value;
  const boxes = store.mailboxes.value;

  // A role only earns a unified row if some account actually has it -- an
  // "Archive" that is empty on every provider is a dead link, not a feature.
  // Computed inline rather than memoised: `useComputed` would capture `boxes`
  // from the first render, which is the render where it is still empty.
  const present = new Set<string>();
  for (const list of boxes.values()) for (const m of list) if (m.role) present.add(m.role);
  const roles = UNIFIED.filter((u) => present.has(u.role));

  const unreadFor = (role: string): number => {
    let n = 0;
    for (const list of boxes.values()) for (const m of list) if (m.role === role) n += m.unreadEmails;
    return n;
  };
  const totalFor = (role: string): number => {
    let n = 0;
    for (const list of boxes.values()) for (const m of list) if (m.role === role) n += m.totalEmails;
    return n;
  };

  return (
    <nav class="col sidebar" data-pane-name="sidebar" tabIndex={-1} aria-label="Mailboxes">
      {/* See `ui/MessageList.tsx` for what `deep` does and why the buttons
          inside are safe. Inert in a browser. */}
      <div class="col-head" data-tauri-drag-region="deep">
        <span class="brand">
          <Icon.Logo class="mark" />
          HydraMail
        </span>
        <span class="spacer" />
        {/*
          Only rendered where the sidebar is a pane rather than a column. It is
          a real labelled control and not a decorative chevron, because on a
          phone it is the only way back to the mail from here that does not
          involve also choosing a folder.
        */}
        <button
          class="icon-btn pane-close"
          aria-label="Close the mailbox list"
          onClick={nav.leaveFolders}
        >
          <Icon.Close />
        </button>
      </div>

      <button class="compose-button" onClick={choose(() => void composeWindow.writeNew())}>
        <Icon.Pencil />
        Compose
      </button>

      <div class="col-body">
        <div class="nav">
          <div class="nav-label">All accounts</div>
          {roles.map((u) => {
            // Trash goes unbadged for the reason `rolledUpUnread` leaves it
            // out: deleted-unread only accumulates, and a number that never
            // reaches zero stops meaning anything. The row is still there;
            // the count is what a person asked "what is that number?" about.
            // Drafts counts *everything*, the way Gmail's does: "unread" is
            // not a state your own unfinished mail meaningfully has, and the
            // useful number is how many messages are waiting to be finished.
            const count =
              u.role === "trash"
                ? 0
                : u.role === "drafts"
                  ? totalFor(u.role)
                  : unreadFor(u.role);
            const active =
              store.view.value === "mail" && scope.kind === "unified" && scope.role === u.role;
            return (
              <button
                key={u.role}
                class="nav-item"
                aria-current={active}
                onClick={choose(() => store.setScope({ kind: "unified", role: u.role }))}
              >
                <u.icon />
                <span class="label">{u.label}</span>
                {count > 0 && <span class="count">{count}</span>}
              </button>
            );
          })}
          {/* Kept next to the rows it is about: the two entries below are not
              unified mailboxes and their being there is no answer to this. */}
          {roles.length === 0 && <div class="nav-label">no mailboxes yet</div>}

          {/*
            Below the folders and not among them, because they are not folders.
            Everything above is "a mailbox in each account"; these two are one
            property of a message asked across every mailbox there is, and a
            reader who takes Starred for a folder will wonder why archiving
            something does not remove it from there.

            Both are gated on there being an account rather than on there being
            a mailbox with some role, which is what the rows above need: a star
            and a pin can be on a message in a folder with no role at all, so
            the account having only folders of its own is no reason to withhold
            either view.
          */}
          {store.accounts.value.length > 0 && (
            <button
              class="nav-item"
              aria-current={store.view.value === "mail" && scope.kind === "starred"}
              title="Every starred message, in every folder"
              onClick={choose(() => store.setScope({ kind: "starred" }))}
            >
              <Icon.Star />
              <span class="label">{store.STARRED_LABEL}</span>
              {/*
                No count, and this is a decision rather than an omission. The
                sidebar's numbers come from the mailbox counters, and those
                count messages and unread messages per folder -- no keyword. So
                "how many starred messages are unread" cannot be read off
                anything already fetched; getting it would mean another
                `Email/query` per account on every refresh, paid for ever, to
                decorate one row. A number assembled out of the counters that
                *are* here would be a different number wearing this one's
                clothes, and this file already has two comments about what a
                badge nobody can trust costs.
              */}
            </button>
          )}

          {/*
            Desktop only, and absent rather than disabled -- `lib/retention.ts`
            states the rule and the reason: a pin is a promise somebody relies
            on in a tunnel, and a control that looks capable of making one and
            is not is worse than no control. In a browser there is no local
            store, so there is nothing this row could show.
          */}
          {/*
            Reminders: server state about messages, like a star and unlike a
            pin, so it is on every device. The count is every reminder set --
            the size of the list behind the row -- and turns red while any
            of them is overdue.
          */}
          {store.accounts.value.length > 0 && (
            <button
              class="nav-item"
              aria-current={store.view.value === "mail" && scope.kind === "reminders"}
              title="Messages with a reminder"
              onClick={choose(() => store.setScope({ kind: "reminders" }))}
            >
              <Icon.Bell />
              <span class="label">{store.REMINDERS_LABEL}</span>
              {reminders.items.value.length > 0 && (
                <span class={`count${reminders.overdue.value > 0 ? " overdue" : ""}`}>
                  {reminders.items.value.length}
                </span>
              )}
            </button>
          )}

          {retention.available && store.accounts.value.length > 0 && (
            <button
              class="nav-item"
              aria-current={store.view.value === "mail" && scope.kind === "saved"}
              title={`${plural(retention.pinnedCount.value, "message")} saved on this device`}
              onClick={choose(() => store.setScope({ kind: "saved" }))}
            >
              <Icon.KeepOffline />
              <span class="label">{store.KEPT_LABEL}</span>
              {/*
                How many are kept, not how many are unread, and it is the one
                row here where that is the right number. Everywhere else the
                badge answers "what still wants me"; here the question a person
                has is "how much am I carrying", and it is also the only figure
                this row could give that is not a second-hand guess. Dimmed by
                `.count.kept` so it does not read as an unread badge sitting
                among four that are.
              */}
              {retention.pinnedCount.value > 0 && (
                <span class="count kept">{retention.pinnedCount.value}</span>
              )}
            </button>
          )}

          {store.accounts.value.map((account) => (
            <AccountSection
              key={account.id}
              id={account.id}
              name={account.name}
              boxes={boxes.get(account.id) ?? []}
            />
          ))}
        </div>
      </div>

      <div class="sidebar-foot">
        {/*
          One way in, and this is it. Accounts used to sit here as its own
          button, from when it was a screen of its own; it is a page of the
          settings shell now, and leaving the shortcut meant the sidebar
          offered two doors into one room -- with only one of them able to
          reach the other settings pages. `view = "accounts"` still exists as
          an alias for the paths that legitimately deep-link to it: the empty
          mailbox prompt, and the return leg of Google's OAuth.
        */}
        <button
          class="icon-btn"
          title="Settings"
          aria-label="Settings"
          onClick={choose(() => (store.view.value = "settings"))}
        >
          <Icon.Gear />
        </button>
        <ThemeToggle />
      </div>
    </nav>
  );
}

/**
 * Unread across a set of mailboxes, for a line that stands in for them.
 *
 * Trash and junk are left out. They are the two folders that accumulate unread
 * mail nobody intends to read, so counting them means a collapsed account sits
 * on a permanent badge — and a badge that never goes to zero is one people stop
 * looking at, which costs the number its only job.
 */
function rolledUpUnread(boxes: Mailbox[]): number {
  let n = 0;
  for (const m of boxes) {
    if (m.role === "trash" || m.role === "junk") continue;
    n += m.unreadEmails;
  }
  return n;
}

/** A mailbox and the mailboxes filed under it. */
interface Node {
  box: Mailbox;
  children: Node[];
}

/**
 * Roles first in the canonical order, then everything else alphabetically: a
 * provider's own ordering is arbitrary and differs between them.
 *
 * Applied per sibling group rather than to the whole account, which is what
 * keeps nesting from moving the folders people navigate by: a top-level Inbox
 * is still the first row under its account, whatever grew underneath it.
 */
const ROLE_ORDER = new Map(
  // `flagged` is in here although it has no unified row any more. The two lists
  // answer different questions: one is "which merged views are worth offering",
  // the other is "where does this folder belong among its siblings" -- and an
  // account that really does have a `\Flagged` folder should still find it
  // sorted where a mail client puts it rather than filed alphabetically among
  // the ones somebody made themselves.
  ["inbox", "flagged", "sent", "archive", "trash"].map((role, i) => [role, i]),
);

function byRoleThenName(a: Mailbox, b: Mailbox): number {
  const ra = ROLE_ORDER.get(a.role ?? "") ?? 99;
  const rb = ROLE_ORDER.get(b.role ?? "") ?? 99;
  return ra - rb || a.name.localeCompare(b.name);
}

/**
 * Arrange an account's mailboxes into the tree their `parentId`s describe.
 *
 * Two things this data does would lose a folder from the sidebar rather than
 * merely draw it oddly, and a folder missing from the sidebar is mail with no
 * way to reach it:
 *
 * A `parentId` can name a mailbox that is not in the list. Gmail's `[Gmail]` is
 * a `\Noselect` placeholder, an unsubscribed parent may never be fetched, and
 * either way the child is real. Those are promoted to the top of the account
 * instead of being filed under something that is not there.
 *
 * And `parentId` is whatever the provider said, so a cycle is possible. Every
 * member of one has a parent that exists, so none of them is a root and the
 * walk below never reaches them; anything still unvisited afterwards is
 * appended at the top. A bad link costs the indent, not the folder.
 *
 * Order comes from the child lists, so a child arriving before its parent — the
 * ordinary case, since the daemon links parents in a second pass — sorts the
 * same as one arriving after.
 */
function tree(boxes: Mailbox[]): Node[] {
  const byId = new Map(boxes.map((m) => [m.id, m]));
  const children = new Map<string, Mailbox[]>();
  const roots: Mailbox[] = [];

  for (const m of boxes) {
    if (m.parentId === null || !byId.has(m.parentId)) {
      roots.push(m);
      continue;
    }
    const kids = children.get(m.parentId);
    if (kids) kids.push(m);
    else children.set(m.parentId, [m]);
  }

  const seen = new Set<string>();
  const build = (list: Mailbox[]): Node[] =>
    [...list].sort(byRoleThenName).map((box) => {
      seen.add(box.id);
      return { box, children: build(children.get(box.id) ?? []) };
    });

  const out = build(roots);
  for (const m of boxes) if (!seen.has(m.id)) out.push({ box: m, children: [] });
  return out;
}

/** Whether the mailbox with this id is anywhere in these subtrees. */
function holds(nodes: Node[], id: string): boolean {
  return nodes.some((n) => n.box.id === id || holds(n.children, id));
}

/** `rolledUpUnread` over a whole subtree, for a folded parent's badge. */
function subtreeUnread(nodes: Node[]): number {
  let n = 0;
  for (const node of nodes) {
    n += rolledUpUnread([node.box]) + subtreeUnread(node.children);
  }
  return n;
}

function AccountSection({ id, name, boxes }: { id: string; name: string; boxes: Mailbox[] }) {
  const scope = store.scope.value;
  const nodes = tree(boxes);

  /*
   * Folding the group that holds the folder you are reading used to be refused
   * outright, so the sidebar always had something marked. That bought the wrong
   * thing: it made the header do nothing on the one account most likely to be
   * clicked, while `aria-expanded` went on reporting the group as open, so the
   * only feedback a screen reader had was the absence of any. A control that
   * silently declines is worse than one that folds.
   *
   * So it folds, and the header says so with `aria-current="location"` -- the
   * token that means "you are in here", as against the `true` a row uses to
   * mean "you are reading this". "Where am I" is still answered on screen, by
   * the row that is still on screen. The nested groups below do the same
   * thing, which is the other reason for the change: the old rule would have
   * had to be repeated at every depth to mean anything.
   */
  const folded = accountCollapsed(id);
  const holdsCurrentScope =
    scope.kind === "mailbox" && scope.accountId === id && store.view.value === "mail";
  const unread = rolledUpUnread(boxes);
  const groupId = `folders-${id}`;

  return (
    <>
      {/*
        A real button, not a div with a click handler: this is the one control
        in the sidebar that changes what the sidebar shows rather than what the
        list shows, and `aria-expanded` is the only thing that says so to
        somebody who cannot see the chevron.

        The colour goes here because this heading is where a person learns which
        colour means which account -- everywhere else in the app is that lesson
        being used -- so it sits on the one line that always carries the full
        name.
      */}
      <button
        class="nav-label nav-fold"
        title={name}
        aria-expanded={!folded}
        aria-controls={groupId}
        aria-current={folded && holdsCurrentScope ? "location" : false}
        onClick={() => toggleAccountCollapsed(id)}
      >
        <Icon.Chevron class={folded ? "icon fold-mark" : "icon fold-mark open"} />
        <AccountDot accountId={id} />
        <span class="label">{name}</span>
        {/*
          Only while folded. Expanded, the same number is already spread across
          the folder rows below, and showing both invites the reader to check
          whether they agree.
        */}
        {folded && unread > 0 && <span class="count">{unread}</span>}
      </button>
      {/*
        Rendered but hidden rather than dropped from the tree. `hidden` keeps it
        out of the accessibility tree and the tab order, which is the part that
        matters, while leaving the folder rows mounted so expanding does not
        re-run the sort or flash an empty group.
      */}
      <div class="nav-group" id={groupId} hidden={folded}>
        <FolderRows accountId={id} nodes={nodes} depth={1} />
      </div>
    </>
  );
}

/**
 * How deep the indent goes before it stops going.
 *
 * The sidebar column is 200px and a name has to survive the indent, the folder
 * icon and the unread count. Past this the rows stop stepping right rather than
 * pushing their names off the edge, so two folders at different depths can end
 * up on the same margin -- which loses a little information and keeps all of
 * the mail. The chevrons still line up with their own children, and `title`
 * still carries the full name.
 *
 * Not a mobile problem: the sidebar is a full-width takeover pane below the
 * three-column breakpoint, so it is widest exactly where the screen is
 * narrowest.
 */
const MAX_INDENT_DEPTH = 4;

function FolderRows({
  accountId,
  nodes,
  depth,
}: {
  accountId: string;
  nodes: Node[];
  depth: number;
}) {
  return (
    <>
      {nodes.map((node) => (
        <FolderRow key={node.box.id} accountId={accountId} node={node} depth={depth} />
      ))}
    </>
  );
}

function FolderRow({
  accountId,
  node,
  depth,
}: {
  accountId: string;
  node: Node;
  depth: number;
}) {
  const scope = store.scope.value;
  const m = node.box;
  const parent = node.children.length > 0;
  const folded = parent && mailboxCollapsed(accountId, m.id);
  const groupId = `folders-${accountId}-${m.id}`;

  const active =
    store.view.value === "mail" && scope.kind === "mailbox" && scope.mailboxId === m.id;
  // Same rule, and the same distinction, as the account header above: this row
  // is not what you are reading, it is where what you are reading went.
  const hidesCurrentScope =
    folded &&
    store.view.value === "mail" &&
    scope.kind === "mailbox" &&
    scope.accountId === accountId &&
    holds(node.children, scope.mailboxId);

  // Folded, the row answers for everything under it, or folding would be a way
  // to make unread mail disappear. Expanded, the children carry their own.
  // Trash and junk carry none either way -- the same permanent-badge rule as
  // `rolledUpUnread`, applied to the rows themselves.
  const count =
    m.role === "trash" || m.role === "junk"
      ? 0
      : folded
        ? m.unreadEmails + subtreeUnread(node.children)
        : m.unreadEmails;

  return (
    <>
      {/*
        The disclosure is a sibling of the folder row, not inside it: a button
        inside a button is not a thing a browser will render, and the two do
        genuinely different jobs -- one opens the folder, one opens the group.
        Being separate elements is also what makes clicking the chevron not
        select the folder, without a handler having to stop anything.

        It is absolutely positioned into the row's indent so that leaves and
        parents keep their folder icons on the same line. Giving parents a real
        first child would either shift them right of their siblings or make
        every leaf carry a matching spacer.
      */}
      <div class="nav-row" style={{ "--depth": String(Math.min(depth, MAX_INDENT_DEPTH)) }}>
        {parent && (
          <button
            class="nav-twisty"
            aria-expanded={!folded}
            aria-controls={groupId}
            aria-label={`${folded ? "Show" : "Hide"} folders in ${m.name}`}
            onClick={() => toggleMailboxCollapsed(accountId, m.id)}
          >
            <Icon.Chevron class={folded ? "icon fold-mark" : "icon fold-mark open"} />
          </button>
        )}
        <button
          class="nav-item nav-sub"
          aria-current={active ? true : hidesCurrentScope ? "location" : false}
          onClick={choose(() =>
            store.setScope({ kind: "mailbox", accountId, mailboxId: m.id }),
          )}
          title={`${m.name} — ${m.totalEmails} total`}
        >
          <Icon.Folder />
          <span class="label">{m.name}</span>
          {count > 0 && <span class="count">{count}</span>}
        </button>
      </div>
      {parent && (
        <div class="nav-group" id={groupId} hidden={folded}>
          <FolderRows accountId={accountId} nodes={node.children} depth={depth + 1} />
        </div>
      )}
    </>
  );
}

function ThemeToggle() {
  const next = theme.value === "dark" ? "light" : "dark";
  return (
    <button
      class="icon-btn"
      title={`Switch to ${next} mode (currently ${theme.value})`}
      aria-label="Toggle dark mode"
      onClick={() => (theme.value = next)}
    >
      {theme.value === "dark" ? <Icon.Sun /> : <Icon.Moon />}
    </button>
  );
}
